import { Select as ArkSelect, useSelectContext, createListCollection } from "@ark-ui/react";
import classnames from "classnames";
import { useEffect, useMemo, ComponentPropsWithoutRef } from "react";
import { styled } from "@mui/material";

import { useTranslation } from "@/lib/i18n";
import { MapLayerController } from "@/lib/map-layers";
import Select from "@/components/select/select";
import Stack from "@/components/stack/stack";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import {
  LayersPanelContainer,
  LegendPanelHeader,
} from "@/features/shared/widgets/map/map.styles";
import styles from "@/components/select/select.module.css";

import legendStyles from "./map-control-legend.module.css";

type LegendItem = { label: string; value: string; group: "active" | "inactive" };

interface MapControlLegendProps {
  layers: MapLayerController[];
  selectedLegendLayerId: string | undefined;
  onSelectedLegendLayerIdChange: (layerId: string) => void;
}

function LegendSelectContent(props: ComponentPropsWithoutRef<typeof ArkSelect.Content>) {
  const { t } = useTranslation();
  const context = useSelectContext();

  const groupLabel = (group: string): string =>
    group === "active" ? t("map.legend_group_active") : t("map.legend_group_inactive");

  return (
    <div
      {...props}
      className={classnames(styles.content, styles.legendSelectContent, props.className)}
      data-part="content"
    >
      {context.collection.group().map(([group, items]) => (
        <ArkSelect.ItemGroup key={group}>
          {group && (
            <ArkSelect.ItemGroupLabel>{groupLabel(group)}</ArkSelect.ItemGroupLabel>
          )}
          {items.map((option) => (
            <ArkSelect.Item key={option.value} item={option} className={styles.item}>
              <ArkSelect.ItemText>{option.label}</ArkSelect.ItemText>
            </ArkSelect.Item>
          ))}
        </ArkSelect.ItemGroup>
      ))}
    </div>
  );
}

function LegendLayerDropdown({
  layers,
  value,
  onValueChange,
}: {
  layers: MapLayerController[];
  value: string | undefined;
  onValueChange: (layerId: string) => void;
}) {
  const collection = useMemo(
    () =>
      createListCollection<LegendItem>({
        items: layers.map((l) => ({
          label: l.title,
          value: l.id,
          group: l.isEnabled ? "active" : "inactive",
        })),
        groupBy: (item) => item.group,
        groupSort: ["active", "inactive"],
      }),
    [layers],
  );

  useEffect(() => {
    const validId = value && layers.some((l) => l.id === value);
    if (!validId && layers.length > 0) {
      onValueChange(layers[0].id);
    }
  }, [layers, value, onValueChange]);

  const showDropdown = layers.length > 1;
  const selectedId = value ?? layers[0]?.id;
  const currentLayer = layers.find((l) => l.id === selectedId) ?? layers[0];

  if (showDropdown) {
    return (
      <Select
        collection={collection}
        value={selectedId}
        onValueChange={onValueChange}
        positioning={{
          sameWidth: false,
          offset: { mainAxis: 8, crossAxis: -8 },
        }}
        variant="ghost"
        onlyLightTheme
        content={<LegendSelectContent />}
      />
    );
  }

  return currentLayer ? <span>{currentLayer.title}</span> : null;
}

const MapControlLegend = ({
  layers,
  selectedLegendLayerId,
  onSelectedLegendLayerIdChange,
}: MapControlLegendProps) => {
  const layersWithLegend = useMemo(
    () => layers.filter((l) => l.isEnabled && l.renderedLegend != null),
    [layers],
  );

  const currentLayer = useMemo(
    () =>
      layersWithLegend.find((l) => l.id === selectedLegendLayerId) ??
      layersWithLegend[0] ??
      null,
    [layersWithLegend, selectedLegendLayerId],
  );

  if (layersWithLegend.length === 0) return null;

  return (
    <LayersPanelContainer
      className={classnames(legendStyles.container, panelCommonStyles.frostedPanel)}
    >
      <LegendPanelHeader>
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          className={legendStyles.titleRow}
        >
          <LegendLayerDropdown
            layers={layersWithLegend}
            value={selectedLegendLayerId}
            onValueChange={onSelectedLegendLayerIdChange}
          />
        </Stack>
      </LegendPanelHeader>
      {currentLayer && <LegendContent>{currentLayer.renderedLegend}</LegendContent>}
    </LayersPanelContainer>
  );
};

const LegendContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
}));

export default MapControlLegend;
